/**
 * PmsSendModal — Modal used to transmit a consultation to the connected PMS.
 * Triggered from a patient's consultation card and from the end of a
 * teleconsultation. Sandbox-only: simulates the transmission with a toast.
 */
import React from "react";
import { toast } from "sonner";
import { ArrowUpRight, Send, ShieldCheck, X } from "lucide-react";
import { Button } from "../../components/ui/button";
import { NGAP_CCAM_CODES } from "./data";

export function PmsSendModal({ patient, visit, onClose }) {
  const [code, setCode] = React.useState((visit?.ngap || "G").split(" ")[0] || "G");
  const [sending, setSending] = React.useState(false);
  const codeInfo = NGAP_CCAM_CODES.find((c) => c.code === code) || NGAP_CCAM_CODES[0];

  const confirm = () => {
    setSending(true);
    setTimeout(() => {
      setSending(false);
      onClose();
      toast.success(`Données transmises au PMS "MediStory 4" · ${patient.first} ${patient.last} · ${codeInfo.code}`);
    }, 600);
  };

  return (
    <div className="fixed inset-0 z-[110] grid place-items-center p-4" onClick={onClose} data-testid="pms-modal">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-lg rounded-2xl bg-white shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg grid place-items-center text-white" style={{ backgroundColor: "#4FC3F7" }}>
              <ArrowUpRight className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[16px] font-bold text-slate-900">Envoyer au PMS</div>
              <div className="text-[11.5px] text-slate-500">Transmission au logiciel métier connecté</div>
            </div>
          </div>
          <button onClick={onClose} data-testid="pms-modal-close" className="p-2 rounded-lg hover:bg-slate-100 text-slate-500">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-1.5">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Résumé de la transmission</div>
            <div className="text-[13px]">
              <span className="text-slate-500">Patient :</span> <span className="font-semibold text-slate-900">{patient.first} {patient.last}</span>
            </div>
            <div className="text-[12.5px] text-slate-600">
              <span className="text-slate-500">INS :</span> <span className="font-mono text-[11.5px]">{patient.ins}</span>
            </div>
            <div className="text-[12.5px] text-slate-600">
              <span className="text-slate-500">Acte réalisé :</span> <span className="font-medium text-slate-900">{visit?.motif || "Consultation"}</span> · {visit?.date}
            </div>
            <div className="text-[12.5px] text-slate-600">
              <span className="text-slate-500">Diagnostic :</span> <span className="text-slate-800">{visit?.diag}</span>
            </div>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold block mb-1.5">Code NGAP / CCAM</label>
            <select
              value={code}
              onChange={(e) => setCode(e.target.value)}
              data-testid="pms-modal-code"
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13.5px] text-slate-900 bg-white"
            >
              {NGAP_CCAM_CODES.map((c) => (
                <option key={c.code} value={c.code}>{c.code} — {c.label}</option>
              ))}
            </select>
            <div className="mt-1 text-[11px] text-slate-500">Le code sélectionné sera envoyé au PMS pour facturation.</div>
          </div>

          <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-[11.5px] text-blue-900 flex items-start gap-2">
            <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0 text-blue-600" />
            <div>
              Transmission chiffrée HTTPS · Authentification Pro Santé Connect · Journal d&apos;audit horodaté.
            </div>
          </div>
        </div>

        <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            data-testid="pms-modal-cancel"
            className="px-4 py-2 rounded-md text-[13px] font-medium text-slate-600 hover:bg-slate-100"
          >
            Annuler
          </button>
          <Button
            onClick={confirm}
            disabled={sending}
            data-testid="pms-modal-confirm"
            className="text-white"
            style={{ backgroundColor: "#4FC3F7" }}
          >
            {sending ? "Transmission…" : "Confirmer l'envoi"} <Send className="w-3.5 h-3.5 ml-1.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
